import { SpellType } from '../game/CooldownManager';

// MediaPipe types - loaded from CDN scripts
declare global {
  interface Window {
    Hands: new (config: { locateFile: (file: string) => string }) => MediaPipeHands;
    Camera: new (video: HTMLVideoElement, config: { onFrame: () => Promise<void>; width: number; height: number }) => MediaPipeCamera;
  }
}

interface MediaPipeHands {
  setOptions(options: {
    maxNumHands: number;
    modelComplexity: number;
    minDetectionConfidence: number;
    minTrackingConfidence: number;
  }): void;
  onResults(callback: (results: Results) => void): void;
  send(input: { image: HTMLVideoElement }): Promise<void>;
}

interface MediaPipeCamera {
  start(): Promise<void>;
  stop(): void;
}

interface NormalizedLandmark {
  x: number;
  y: number;
  z: number;
}

interface HandednessEntry {
  label: 'Left' | 'Right';
  score: number;
}

interface Results {
  multiHandLandmarks?: NormalizedLandmark[][];
  multiHandedness?: HandednessEntry[][];
}

export type GestureShape = 'circle' | 'swipe-down' | 'palm-hold' | 'zigzag' | 'swipe-up' | 'palm-push' | 'flick-horizontal';

export interface GestureInfo {
  spell: SpellType;
  shape: GestureShape;
  confidence: number;
  handedness?: 'Left' | 'Right';
  twoHanded?: boolean;
}

export interface GestureProgress {
  gesture: string;
  progress: number; // 0-1
}

export type GestureCallback = (gesture: SpellType, info?: GestureInfo) => void;

interface Point {
  x: number;
  y: number;
  timestamp: number;
}

// Per-hand tracking state for two-handed detection
interface HandTrackingState {
  indexFingerPath: Point[];
  palmPositions: Point[];
  handSizeHistory: { size: number; timestamp: number }[];
  openPalmStartTime: number;
  handedness: 'Left' | 'Right' | null;
}

// Records a gesture detected on a single hand, for two-hand matching
interface SingleHandGestureEvent {
  shape: GestureShape;
  spell: SpellType;
  confidence: number;
  handedness: 'Left' | 'Right' | null;
  timestamp: number;
}

export class GestureDetector {
  private hands: MediaPipeHands | null = null;
  private camera: MediaPipeCamera | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private canvasElement: HTMLCanvasElement | null = null;
  private canvasCtx: CanvasRenderingContext2D | null = null;
  private onGestureCallback: GestureCallback | null = null;
  private isInitialized: boolean = false;

  // Primary hand tracking (backward-compatible)
  private indexFingerPath: Point[] = [];
  private palmPositions: Point[] = [];
  private handSizeHistory: { size: number; timestamp: number }[] = [];
  private openPalmStartTime: number = 0;
  private lastGestureTime: number = 0;
  private gestureDebounce: number = 500;
  private currentHandedness: 'Left' | 'Right' | null = null;

  // Two-hand tracking
  private secondHandState: HandTrackingState = {
    indexFingerPath: [],
    palmPositions: [],
    handSizeHistory: [],
    openPalmStartTime: 0,
    handedness: null,
  };
  private twoHandsDetected: boolean = false;
  private recentSingleHandGestures: SingleHandGestureEvent[] = [];
  private readonly TWO_HAND_WINDOW_MS = 500; // Both hands must gesture within 500ms

  // Gesture progress for visual feedback
  private currentProgress: GestureProgress | null = null;

  private readonly CIRCLE_THRESHOLD = 0.55;
  private readonly SWIPE_THRESHOLD = 0.15;
  private readonly ZIGZAG_THRESHOLD = 0.6;
  private readonly SWIPE_UP_THRESHOLD = 0.15;
  private readonly PALM_HOLD_TIME = 400;
  private readonly FLICK_HORIZONTAL_THRESHOLD = 0.15;
  private readonly PATH_HISTORY_SIZE = 30;

  async init(): Promise<void> {
    console.log('GestureDetector: Starting init...');

    this.videoElement = document.getElementById('webcam') as HTMLVideoElement;
    this.canvasElement = document.getElementById('gesture-canvas') as HTMLCanvasElement;

    console.log('GestureDetector: Video element:', this.videoElement ? 'found' : 'NOT FOUND');
    console.log('GestureDetector: Canvas element:', this.canvasElement ? 'found' : 'NOT FOUND');

    if (!this.videoElement || !this.canvasElement) {
      throw new Error('Required video or canvas elements not found');
    }

    // Set canvas size to match video dimensions
    this.canvasElement.width = 320;
    this.canvasElement.height = 240;

    this.canvasCtx = this.canvasElement.getContext('2d');

    console.log('GestureDetector: Creating MediaPipe Hands instance...');

    // Check if MediaPipe loaded from CDN
    if (!window.Hands) {
      throw new Error('MediaPipe Hands not loaded. Check CDN script in index.html');
    }

    this.hands = new window.Hands({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`;
      }
    });

    this.hands.setOptions({
      maxNumHands: 2, // Enable two-hand tracking for ultimate spells
      modelComplexity: 1,
      minDetectionConfidence: 0.7,
      minTrackingConfidence: 0.5
    });

    this.hands.onResults(this.onResults.bind(this));

    console.log('GestureDetector: Creating camera instance...');

    // Check if Camera loaded from CDN
    if (!window.Camera) {
      throw new Error('MediaPipe Camera not loaded. Check CDN script in index.html');
    }

    this.camera = new window.Camera(this.videoElement, {
      onFrame: async () => {
        if (this.hands && this.videoElement) {
          await this.hands.send({ image: this.videoElement });
        }
      },
      width: 320,
      height: 240
    });

    console.log('GestureDetector: Starting camera...');
    await this.camera.start();
    this.isInitialized = true;
    console.log('GestureDetector: Initialized successfully (two-hand tracking enabled)');
  }

  private onResults(results: Results): void {
    if (!this.canvasCtx || !this.canvasElement) return;

    try {
      this.canvasCtx.save();
      this.canvasCtx.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);

      const handCount = results.multiHandLandmarks?.length ?? 0;
      this.twoHandsDetected = handCount >= 2;

      if (results.multiHandLandmarks && handCount > 0) {
        // Process first hand (primary)
        const landmarks0 = results.multiHandLandmarks[0];
        if (results.multiHandedness && results.multiHandedness.length > 0) {
          const handedness = results.multiHandedness[0];
          if (handedness.length > 0) {
            this.currentHandedness = handedness[0].label;
          }
        }
        this.drawHandLandmarks(landmarks0, '#C9A227');
        this.trackGestures(landmarks0);

        // Process second hand if present
        if (handCount >= 2) {
          const landmarks1 = results.multiHandLandmarks[1];
          if (results.multiHandedness && results.multiHandedness.length > 1) {
            const handedness1 = results.multiHandedness[1];
            if (handedness1.length > 0) {
              this.secondHandState.handedness = handedness1[0].label;
            }
          }
          this.drawHandLandmarks(landmarks1, '#47E8C5'); // Different color for second hand
          this.trackSecondHandGestures(landmarks1);
        } else {
          this.secondHandState.handedness = null;
        }

        this.drawGestureProgress();
        this.drawTwoHandIndicator();
      } else {
        this.currentHandedness = null;
        this.secondHandState.handedness = null;
        this.twoHandsDetected = false;
      }

      this.canvasCtx.restore();
    } catch (error) {
      console.warn('Gesture detection error:', error);
    }
  }

  private drawHandLandmarks(landmarks: NormalizedLandmark[], color: string = '#C9A227'): void {
    if (!this.canvasCtx || !this.canvasElement) return;

    const width = this.canvasElement.width;
    const height = this.canvasElement.height;

    this.canvasCtx.fillStyle = color;
    this.canvasCtx.strokeStyle = color;
    this.canvasCtx.lineWidth = 2;

    const connections = [
      [0, 1], [1, 2], [2, 3], [3, 4],
      [0, 5], [5, 6], [6, 7], [7, 8],
      [5, 9], [9, 10], [10, 11], [11, 12],
      [9, 13], [13, 14], [14, 15], [15, 16],
      [13, 17], [17, 18], [18, 19], [19, 20],
      [0, 17]
    ];

    connections.forEach(([i, j]) => {
      const start = landmarks[i];
      const end = landmarks[j];

      this.canvasCtx!.beginPath();
      this.canvasCtx!.moveTo(start.x * width, start.y * height);
      this.canvasCtx!.lineTo(end.x * width, end.y * height);
      this.canvasCtx!.stroke();
    });

    landmarks.forEach((landmark, index) => {
      const x = landmark.x * width;
      const y = landmark.y * height;
      const radius = index === 8 ? 6 : 4;

      this.canvasCtx!.beginPath();
      this.canvasCtx!.arc(x, y, radius, 0, Math.PI * 2);
      this.canvasCtx!.fill();
    });

    if (this.indexFingerPath.length > 1) {
      this.canvasCtx.strokeStyle = '#FF6B35';
      this.canvasCtx.lineWidth = 3;
      this.canvasCtx.beginPath();
      this.canvasCtx.moveTo(
        this.indexFingerPath[0].x * width,
        this.indexFingerPath[0].y * height
      );
      for (let i = 1; i < this.indexFingerPath.length; i++) {
        this.canvasCtx.lineTo(
          this.indexFingerPath[i].x * width,
          this.indexFingerPath[i].y * height
        );
      }
      this.canvasCtx.stroke();
    }

    // Draw handedness label
    if (this.currentHandedness) {
      this.canvasCtx.font = '12px monospace';
      this.canvasCtx.fillStyle = '#E8C547';
      this.canvasCtx.fillText(
        `${this.currentHandedness} Hand`,
        5, height - 5
      );
    }
  }

  /** Draw "ULTIMATE READY" indicator when two hands are detected */
  private drawTwoHandIndicator(): void {
    if (!this.canvasCtx || !this.canvasElement || !this.twoHandsDetected) return;

    const width = this.canvasElement.width;
    const ctx = this.canvasCtx;

    // Pulsing "ULTIMATE READY" text
    const pulse = 0.7 + Math.sin(Date.now() * 0.008) * 0.3;

    ctx.font = 'bold 14px monospace';
    ctx.fillStyle = `rgba(255, 215, 0, ${pulse})`;
    ctx.textAlign = 'center';
    ctx.fillText('ULTIMATE READY', width / 2, 36);
    ctx.textAlign = 'start';

    // Second hand label
    if (this.secondHandState.handedness) {
      ctx.font = '12px monospace';
      ctx.fillStyle = '#47E8C5';
      ctx.fillText(
        `${this.secondHandState.handedness} Hand`,
        width - 90, this.canvasElement.height - 5
      );
    }
  }

  /** Draw gesture progress indicator on the canvas overlay */
  private drawGestureProgress(): void {
    if (!this.canvasCtx || !this.canvasElement || !this.currentProgress) return;

    const width = this.canvasElement.width;
    const ctx = this.canvasCtx;
    const progress = this.currentProgress;

    // Draw progress bar at top of canvas
    const barWidth = width - 20;
    const barHeight = 8;
    const barX = 10;
    const barY = 8;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(barX, barY, barWidth, barHeight);

    // Progress fill - color based on gesture type
    const colors: Record<string, string> = {
      'circle': '#FF6B35',
      'swipe-down': '#87CEEB',
      'zigzag': '#FFD700',
      'swipe-up': '#44FF44',
      'palm-hold': '#9B59B6',
      'palm-push': '#88CCFF',
      'flick-horizontal': '#AA66FF'
    };
    ctx.fillStyle = colors[progress.gesture] || '#FFFFFF';
    ctx.fillRect(barX, barY, barWidth * progress.progress, barHeight);

    // Border
    ctx.strokeStyle = '#C9A227';
    ctx.lineWidth = 1;
    ctx.strokeRect(barX, barY, barWidth, barHeight);

    // Label
    ctx.font = '10px monospace';
    ctx.fillStyle = '#E8DCC4';
    ctx.fillText(
      `${progress.gesture} ${Math.round(progress.progress * 100)}%`,
      barX, barY + barHeight + 12
    );
  }

  private trackGestures(landmarks: NormalizedLandmark[]): void {
    const now = Date.now();

    const indexTip = landmarks[8];
    this.indexFingerPath.push({
      x: indexTip.x,
      y: indexTip.y,
      timestamp: now
    });

    const palmCenter = this.calculatePalmCenter(landmarks);
    this.palmPositions.push({
      x: palmCenter.x,
      y: palmCenter.y,
      timestamp: now
    });

    // Increased capture window from 1000ms to 2000ms for better gesture detection
    const cutoffTime = now - 2000;
    this.indexFingerPath = this.indexFingerPath.filter(p => p.timestamp > cutoffTime);
    this.palmPositions = this.palmPositions.filter(p => p.timestamp > cutoffTime);

    if (this.indexFingerPath.length > this.PATH_HISTORY_SIZE) {
      this.indexFingerPath = this.indexFingerPath.slice(-this.PATH_HISTORY_SIZE);
    }
    if (this.palmPositions.length > this.PATH_HISTORY_SIZE) {
      this.palmPositions = this.palmPositions.slice(-this.PATH_HISTORY_SIZE);
    }

    // Compute gesture scores for progress visualization even during debounce
    const circleScore = this.detectCircle();
    const swipeDown = this.detectSwipeDown();
    const zigzagScore = this.detectZigzag();
    const swipeUp = this.detectSwipeUp();
    const flickHorizontal = this.detectFlickHorizontal();

    // Update progress display with the strongest partial gesture
    this.updateGestureProgress(circleScore, swipeDown, zigzagScore, swipeUp, flickHorizontal);

    if (now - this.lastGestureTime < this.gestureDebounce) {
      return;
    }

    const isOpenPalm = this.isOpenPalm(landmarks);
    const shieldDetected = this.detectShield(landmarks, isOpenPalm, now);

    // Check gestures in priority order (most specific first to avoid conflicts)
    // Zigzag is checked before swipe-down because a zigzag contains vertical movement
    if (zigzagScore > this.ZIGZAG_THRESHOLD) {
      this.handleSingleHandGesture('lightningBolt', 'zigzag', zigzagScore, now);
      this.openPalmStartTime = 0;
      return;
    }

    if (circleScore > this.CIRCLE_THRESHOLD) {
      this.handleSingleHandGesture('fireball', 'circle', circleScore, now);
      this.openPalmStartTime = 0;
      return;
    }

    if (swipeUp > this.SWIPE_UP_THRESHOLD) {
      this.handleSingleHandGesture('healSpell', 'swipe-up', Math.min(1, swipeUp * 2), now);
      this.openPalmStartTime = 0;
      return;
    }

    if (swipeDown > this.SWIPE_THRESHOLD) {
      this.handleSingleHandGesture('iceShard', 'swipe-down', Math.min(1, swipeDown * 2), now);
      this.openPalmStartTime = 0;
      return;
    }

    // Teleport: quick horizontal flick (fast left or right swipe)
    if (flickHorizontal > this.FLICK_HORIZONTAL_THRESHOLD) {
      this.handleSingleHandGesture('teleport', 'flick-horizontal', Math.min(1, flickHorizontal * 2), now);
      this.openPalmStartTime = 0;
      return;
    }

    // Reflect: palm push (open palm pushed forward quickly)
    const palmPushDetected = this.detectPalmPush(landmarks, isOpenPalm);
    if (palmPushDetected) {
      this.handleSingleHandGesture('reflect', 'palm-push', 1.0, now);
      this.openPalmStartTime = 0;
      return;
    }

    if (shieldDetected) {
      this.handleSingleHandGesture('shield', 'palm-hold', 1.0, now);
      return;
    }
  }

  /**
   * Track gestures on the second hand. Uses the same detection algorithms
   * but with separate tracking state. When a gesture matches on this hand,
   * we check if the primary hand made the same gesture within the window.
   */
  private trackSecondHandGestures(landmarks: NormalizedLandmark[]): void {
    const now = Date.now();
    const state = this.secondHandState;

    const indexTip = landmarks[8];
    state.indexFingerPath.push({ x: indexTip.x, y: indexTip.y, timestamp: now });

    const palmCenter = this.calculatePalmCenter(landmarks);
    state.palmPositions.push({ x: palmCenter.x, y: palmCenter.y, timestamp: now });

    const cutoffTime = now - 2000;
    state.indexFingerPath = state.indexFingerPath.filter(p => p.timestamp > cutoffTime);
    state.palmPositions = state.palmPositions.filter(p => p.timestamp > cutoffTime);

    if (state.indexFingerPath.length > this.PATH_HISTORY_SIZE) {
      state.indexFingerPath = state.indexFingerPath.slice(-this.PATH_HISTORY_SIZE);
    }
    if (state.palmPositions.length > this.PATH_HISTORY_SIZE) {
      state.palmPositions = state.palmPositions.slice(-this.PATH_HISTORY_SIZE);
    }

    if (now - this.lastGestureTime < this.gestureDebounce) {
      return;
    }

    // Run detection algorithms using second hand's path data
    const circleScore = this.detectCircleFromPath(state.indexFingerPath);
    const swipeDown = this.detectSwipeDownFromPath(state.indexFingerPath);
    const isOpenPalm = this.isOpenPalm(landmarks);
    const palmHeld = this.detectPalmHoldForSecondHand(landmarks, isOpenPalm, now, state);

    // Check the three gesture types that map to ultimate spells:
    // Circle -> Meteor, Palm hold -> Super Shield, Swipe down -> Blizzard
    if (circleScore > this.CIRCLE_THRESHOLD) {
      this.recordSecondHandGesture('circle', 'fireball', circleScore, now);
    }
    if (palmHeld) {
      this.recordSecondHandGesture('palm-hold', 'shield', 1.0, now);
    }
    if (swipeDown > this.SWIPE_THRESHOLD) {
      this.recordSecondHandGesture('swipe-down', 'iceShard', Math.min(1, swipeDown * 2), now);
    }
  }

  /** Record a gesture detected on the second hand and check for two-hand match */
  private recordSecondHandGesture(shape: GestureShape, _spell: SpellType, confidence: number, now: number): void {
    // Check if the primary hand recently detected the same gesture shape
    const matchingPrimary = this.recentSingleHandGestures.find(
      g => g.shape === shape && (now - g.timestamp) < this.TWO_HAND_WINDOW_MS
    );

    if (matchingPrimary) {
      // Two-hand gesture matched -- trigger ultimate spell
      const ultimateSpell = this.mapToUltimateSpell(shape);
      if (ultimateSpell) {
        // Clear the matching primary gesture so it doesn't also fire as single-hand
        this.recentSingleHandGestures = [];
        this.triggerGesture(ultimateSpell, {
          spell: ultimateSpell,
          shape,
          confidence: Math.max(matchingPrimary.confidence, confidence),
          twoHanded: true,
        });
        // Reset second hand state
        this.secondHandState.indexFingerPath = [];
        this.secondHandState.palmPositions = [];
        this.secondHandState.openPalmStartTime = 0;
        this.secondHandState.handSizeHistory = [];
      }
    }
  }

  /** Map a gesture shape to its two-handed ultimate spell */
  private mapToUltimateSpell(shape: GestureShape): SpellType | null {
    switch (shape) {
      case 'circle': return 'meteor';       // Both hands draw circles
      case 'palm-hold': return 'superShield'; // Both palms held steady
      case 'swipe-down': return 'blizzard';   // Both hands swipe down
      default: return null;
    }
  }

  /**
   * Handle a gesture from the primary hand. If two hands are detected,
   * record it for potential two-hand matching. Otherwise fire immediately.
   */
  private handleSingleHandGesture(spell: SpellType, shape: GestureShape, confidence: number, now: number): void {
    if (this.twoHandsDetected) {
      // Two hands visible: record and wait briefly for second hand match
      this.recentSingleHandGestures.push({
        shape,
        spell,
        confidence,
        handedness: this.currentHandedness,
        timestamp: now,
      });

      // Clean old events
      this.recentSingleHandGestures = this.recentSingleHandGestures.filter(
        g => (now - g.timestamp) < this.TWO_HAND_WINDOW_MS * 2
      );

      // Check if second hand already has a matching gesture recorded
      // (the second hand might have fired first)
      const ultimateSpell = this.mapToUltimateSpell(shape);
      if (ultimateSpell) {
        // We'll let the second hand's tracker handle the match via recordSecondHandGesture
        // For now, fire the single-hand gesture with a small delay so the two-hand check
        // can preempt it. Use a microtask to allow same-frame matching.
        setTimeout(() => {
          // Only fire single-hand if the ultimate wasn't triggered
          if (this.recentSingleHandGestures.some(g => g.shape === shape && g.timestamp === now)) {
            // Two-hand match didn't happen, fire single-hand
            this.recentSingleHandGestures = this.recentSingleHandGestures.filter(
              g => !(g.shape === shape && g.timestamp === now)
            );
            this.triggerGesture(spell, {
              spell,
              shape,
              confidence,
              handedness: this.currentHandedness ?? undefined,
              twoHanded: false,
            });
          }
        }, 50); // 50ms grace period for second hand to match
      } else {
        // Not an ultimate-eligible gesture shape, fire immediately
        this.recentSingleHandGestures = this.recentSingleHandGestures.filter(
          g => !(g.shape === shape && g.timestamp === now)
        );
        this.triggerGesture(spell, {
          spell,
          shape,
          confidence,
          handedness: this.currentHandedness ?? undefined,
          twoHanded: false,
        });
      }
    } else {
      // Single hand only -- fire immediately as normal
      this.triggerGesture(spell, {
        spell,
        shape,
        confidence,
        handedness: this.currentHandedness ?? undefined,
        twoHanded: false,
      });
    }
  }

  /** Detect circle gesture from an arbitrary finger path (used for second hand) */
  private detectCircleFromPath(path: Point[]): number {
    if (path.length < 12) return 0;

    const points = path.slice(-20);

    let centerX = 0, centerY = 0;
    points.forEach(p => { centerX += p.x; centerY += p.y; });
    centerX /= points.length;
    centerY /= points.length;

    let avgRadius = 0;
    points.forEach(p => {
      avgRadius += Math.sqrt((p.x - centerX) ** 2 + (p.y - centerY) ** 2);
    });
    avgRadius /= points.length;

    if (avgRadius < 0.025 || avgRadius > 0.35) return 0;

    const distances = points.map(p =>
      Math.sqrt((p.x - centerX) ** 2 + (p.y - centerY) ** 2)
    );
    const sortedDists = [...distances].sort((a, b) => a - b);
    const medianDist = sortedDists[Math.floor(sortedDists.length / 2)];
    const deviations = distances.map(d => Math.abs(d - medianDist));
    const sortedDevs = [...deviations].sort((a, b) => a - b);
    const mad = sortedDevs[Math.floor(sortedDevs.length / 2)];

    const uniformity = 1 - Math.min(1, (mad / medianDist) * 2);

    let totalAngle = 0;
    for (let i = 1; i < points.length; i++) {
      const prev = Math.atan2(points[i - 1].y - centerY, points[i - 1].x - centerX);
      const curr = Math.atan2(points[i].y - centerY, points[i].x - centerX);
      let diff = curr - prev;
      if (diff > Math.PI) diff -= 2 * Math.PI;
      if (diff < -Math.PI) diff += 2 * Math.PI;
      totalAngle += diff;
    }

    const completion = Math.min(1, Math.abs(totalAngle) / (Math.PI * 1.5));
    return uniformity * completion;
  }

  /** Detect swipe-down from an arbitrary finger path (used for second hand) */
  private detectSwipeDownFromPath(path: Point[]): number {
    if (path.length < 8) return 0;

    const recentPoints = path.slice(-10);
    const firstPoint = recentPoints[0];
    const lastPoint = recentPoints[recentPoints.length - 1];

    const deltaY = lastPoint.y - firstPoint.y;
    const deltaX = Math.abs(lastPoint.x - firstPoint.x);

    const timeDelta = lastPoint.timestamp - firstPoint.timestamp;
    if (timeDelta > 400) return 0;

    if (deltaY > 0 && deltaY > deltaX * 2) {
      const speed = deltaY / (timeDelta / 1000);
      if (speed > 0.3) {
        return Math.min(1, deltaY);
      }
    }

    return 0;
  }

  /** Detect palm hold for the second hand (separate state tracking) */
  private detectPalmHoldForSecondHand(
    landmarks: NormalizedLandmark[],
    isOpenPalm: boolean,
    now: number,
    state: HandTrackingState,
  ): boolean {
    // Track hand size
    const wrist = landmarks[0];
    const middleBase = landmarks[9];
    const handSize = Math.sqrt(
      (wrist.x - middleBase.x) ** 2 + (wrist.y - middleBase.y) ** 2
    );
    state.handSizeHistory.push({ size: handSize, timestamp: now });
    state.handSizeHistory = state.handSizeHistory.filter(h => now - h.timestamp < 2000);

    if (!isOpenPalm) {
      state.openPalmStartTime = 0;
      return false;
    }

    // Check palm steadiness
    if (state.palmPositions.length >= 5) {
      const recentPositions = state.palmPositions.slice(-5);
      let totalMovement = 0;
      for (let i = 1; i < recentPositions.length; i++) {
        const dx = recentPositions[i].x - recentPositions[i - 1].x;
        const dy = recentPositions[i].y - recentPositions[i - 1].y;
        totalMovement += Math.sqrt(dx * dx + dy * dy);
      }
      if (totalMovement > 0.1) {
        state.openPalmStartTime = 0;
        return false;
      }
    }

    if (state.openPalmStartTime === 0) {
      state.openPalmStartTime = now;
      return false;
    }

    if (now - state.openPalmStartTime >= this.PALM_HOLD_TIME) {
      state.openPalmStartTime = 0;
      return true;
    }

    return false;
  }

  /** Update gesture progress for visual feedback */
  private updateGestureProgress(
    circleScore: number,
    swipeDown: number,
    zigzagScore: number,
    swipeUp: number,
    flickHorizontal: number
  ): void {
    // Normalize scores relative to their thresholds so 1.0 = triggered
    const scores: { gesture: string; progress: number }[] = [
      { gesture: 'circle', progress: circleScore / this.CIRCLE_THRESHOLD },
      { gesture: 'swipe-down', progress: swipeDown / this.SWIPE_THRESHOLD },
      { gesture: 'zigzag', progress: zigzagScore / this.ZIGZAG_THRESHOLD },
      { gesture: 'swipe-up', progress: swipeUp / this.SWIPE_UP_THRESHOLD },
      { gesture: 'flick-horizontal', progress: flickHorizontal / this.FLICK_HORIZONTAL_THRESHOLD },
    ];

    // Show the strongest partial gesture (only if above 20% of threshold)
    const best = scores.reduce((a, b) => a.progress > b.progress ? a : b);
    if (best.progress > 0.2) {
      this.currentProgress = {
        gesture: best.gesture,
        progress: Math.min(1, best.progress)
      };
    } else {
      this.currentProgress = null;
    }
  }

  private calculatePalmCenter(landmarks: NormalizedLandmark[]): { x: number; y: number } {
    const palmIndices = [0, 5, 9, 13, 17];
    let x = 0, y = 0;
    palmIndices.forEach(i => {
      x += landmarks[i].x;
      y += landmarks[i].y;
    });
    return { x: x / palmIndices.length, y: y / palmIndices.length };
  }

  private isOpenPalm(landmarks: NormalizedLandmark[]): boolean {
    const fingerTips = [8, 12, 16, 20];
    const fingerBases = [5, 9, 13, 17];

    let extendedCount = 0;
    for (let i = 0; i < fingerTips.length; i++) {
      const tip = landmarks[fingerTips[i]];
      const base = landmarks[fingerBases[i]];
      if (tip.y < base.y) {
        extendedCount++;
      }
    }

    const thumbTip = landmarks[4];
    const thumbBase = landmarks[2];
    if (Math.abs(thumbTip.x - thumbBase.x) > 0.05) {
      extendedCount++;
    }

    return extendedCount >= 4;
  }

  private detectCircle(): number {
    if (this.indexFingerPath.length < 12) return 0; // Lowered from 15 for faster detection

    const points = this.indexFingerPath.slice(-20);

    let centerX = 0, centerY = 0;
    points.forEach(p => {
      centerX += p.x;
      centerY += p.y;
    });
    centerX /= points.length;
    centerY /= points.length;

    let avgRadius = 0;
    points.forEach(p => {
      avgRadius += Math.sqrt((p.x - centerX) ** 2 + (p.y - centerY) ** 2);
    });
    avgRadius /= points.length;

    if (avgRadius < 0.025 || avgRadius > 0.35) return 0; // Widened radius range

    // Use median absolute deviation instead of variance for more robust outlier handling
    const distances = points.map(p =>
      Math.sqrt((p.x - centerX) ** 2 + (p.y - centerY) ** 2)
    );
    const sortedDists = [...distances].sort((a, b) => a - b);
    const medianDist = sortedDists[Math.floor(sortedDists.length / 2)];
    const deviations = distances.map(d => Math.abs(d - medianDist));
    const sortedDevs = [...deviations].sort((a, b) => a - b);
    const mad = sortedDevs[Math.floor(sortedDevs.length / 2)];

    // Uniformity based on MAD relative to median radius - more forgiving than variance
    const uniformity = 1 - Math.min(1, (mad / medianDist) * 2);

    let totalAngle = 0;
    for (let i = 1; i < points.length; i++) {
      const prev = Math.atan2(points[i - 1].y - centerY, points[i - 1].x - centerX);
      const curr = Math.atan2(points[i].y - centerY, points[i].x - centerX);
      let diff = curr - prev;
      if (diff > Math.PI) diff -= 2 * Math.PI;
      if (diff < -Math.PI) diff += 2 * Math.PI;
      totalAngle += diff;
    }

    const completion = Math.min(1, Math.abs(totalAngle) / (Math.PI * 1.5));

    return uniformity * completion;
  }

  private detectSwipeDown(): number {
    if (this.indexFingerPath.length < 8) return 0;

    const recentPoints = this.indexFingerPath.slice(-10);
    const firstPoint = recentPoints[0];
    const lastPoint = recentPoints[recentPoints.length - 1];

    const deltaY = lastPoint.y - firstPoint.y;
    const deltaX = Math.abs(lastPoint.x - firstPoint.x);

    const timeDelta = lastPoint.timestamp - firstPoint.timestamp;
    if (timeDelta > 400) return 0;

    if (deltaY > 0 && deltaY > deltaX * 2) {
      const speed = deltaY / (timeDelta / 1000);
      if (speed > 0.3) {
        return Math.min(1, deltaY);
      }
    }

    return 0;
  }

  /** Detect upward swipe gesture for heal spell */
  private detectSwipeUp(): number {
    if (this.indexFingerPath.length < 8) return 0;

    const recentPoints = this.indexFingerPath.slice(-10);
    const firstPoint = recentPoints[0];
    const lastPoint = recentPoints[recentPoints.length - 1];

    // deltaY is negative for upward movement (y increases downward in screen coords)
    const deltaY = firstPoint.y - lastPoint.y;
    const deltaX = Math.abs(lastPoint.x - firstPoint.x);

    const timeDelta = lastPoint.timestamp - firstPoint.timestamp;
    if (timeDelta > 500) return 0;

    // Must move upward significantly more than horizontally
    if (deltaY > 0 && deltaY > deltaX * 2) {
      const speed = deltaY / (timeDelta / 1000);
      if (speed > 0.3) {
        return Math.min(1, deltaY);
      }
    }

    return 0;
  }

  /**
   * Detect zigzag motion for lightning bolt.
   * A zigzag is rapid horizontal direction changes while moving vertically.
   * We count the number of horizontal direction reversals in recent path points.
   */
  private detectZigzag(): number {
    if (this.indexFingerPath.length < 10) return 0;

    const points = this.indexFingerPath.slice(-15);
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];
    const timeDelta = lastPoint.timestamp - firstPoint.timestamp;

    // Must happen within a reasonable time window
    if (timeDelta > 800 || timeDelta < 100) return 0;

    // Count horizontal direction reversals
    let reversals = 0;
    let prevDx = 0;
    let totalHorizontalTravel = 0;
    let totalVerticalTravel = 0;

    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      const dy = points[i].y - points[i - 1].y;

      totalHorizontalTravel += Math.abs(dx);
      totalVerticalTravel += Math.abs(dy);

      // Only count meaningful direction changes (ignore tiny noise)
      if (Math.abs(dx) > 0.005) {
        if (prevDx !== 0 && Math.sign(dx) !== Math.sign(prevDx)) {
          reversals++;
        }
        prevDx = dx;
      }
    }

    // Need at least 3 reversals for a zigzag (left-right-left-right pattern)
    if (reversals < 3) return 0;

    // Must have significant horizontal travel (not just noise)
    if (totalHorizontalTravel < 0.06) return 0;

    // Horizontal travel should be meaningful relative to vertical
    // A zigzag has both horizontal and vertical extent
    const totalTravel = totalHorizontalTravel + totalVerticalTravel;
    if (totalTravel < 0.1) return 0;

    // Score based on number of reversals and speed
    const reversalScore = Math.min(1, (reversals - 2) / 4); // 3 reversals = 0.25, 6+ = 1.0
    const speedScore = Math.min(1, totalTravel / (timeDelta / 1000) / 0.5);

    return reversalScore * speedScore;
  }

  private detectShield(landmarks: NormalizedLandmark[], isOpenPalm: boolean, now: number): boolean {
    // Track hand size for push detection
    const wrist = landmarks[0];
    const middleBase = landmarks[9];
    const handSize = Math.sqrt(
      (wrist.x - middleBase.x) ** 2 + (wrist.y - middleBase.y) ** 2
    );

    this.handSizeHistory.push({ size: handSize, timestamp: now });
    // Keep only last 2 seconds of history (matching gesture capture window)
    this.handSizeHistory = this.handSizeHistory.filter(h => now - h.timestamp < 2000);

    if (!isOpenPalm) {
      this.openPalmStartTime = 0;
      return false;
    }

    // Check if palm is relatively steady (not moving too much)
    if (this.palmPositions.length >= 5) {
      const recentPositions = this.palmPositions.slice(-5);
      let totalMovement = 0;
      for (let i = 1; i < recentPositions.length; i++) {
        const dx = recentPositions[i].x - recentPositions[i - 1].x;
        const dy = recentPositions[i].y - recentPositions[i - 1].y;
        totalMovement += Math.sqrt(dx * dx + dy * dy);
      }

      // If hand is moving too much, reset timer
      if (totalMovement > 0.1) {
        this.openPalmStartTime = 0;
        return false;
      }
    }

    // Start timing if open palm just detected
    if (this.openPalmStartTime === 0) {
      this.openPalmStartTime = now;
      return false;
    }

    // Update progress for palm hold
    const palmProgress = (now - this.openPalmStartTime) / this.PALM_HOLD_TIME;
    if (palmProgress > 0.2) {
      this.currentProgress = {
        gesture: 'palm-hold',
        progress: Math.min(1, palmProgress)
      };
    }

    // Check if held long enough
    if (now - this.openPalmStartTime >= this.PALM_HOLD_TIME) {
      this.openPalmStartTime = 0;
      return true;
    }

    return false;
  }

  /**
   * Detect a quick horizontal flick for Teleport.
   * Similar to swipe-down but horizontal: fast left or right movement
   * with minimal vertical displacement.
   */
  private detectFlickHorizontal(): number {
    if (this.indexFingerPath.length < 8) return 0;

    const recentPoints = this.indexFingerPath.slice(-10);
    const firstPoint = recentPoints[0];
    const lastPoint = recentPoints[recentPoints.length - 1];

    const deltaX = Math.abs(lastPoint.x - firstPoint.x);
    const deltaY = Math.abs(lastPoint.y - firstPoint.y);

    const timeDelta = lastPoint.timestamp - firstPoint.timestamp;
    if (timeDelta > 400) return 0;

    // Horizontal movement must dominate vertical (opposite of swipe-down)
    if (deltaX > 0 && deltaX > deltaY * 2) {
      const speed = deltaX / (timeDelta / 1000);
      if (speed > 0.4) {
        return Math.min(1, deltaX);
      }
    }

    return 0;
  }

  /**
   * Detect a palm push for Reflect.
   * Open palm pushed forward (hand size increases rapidly),
   * indicating a pushing gesture toward the screen.
   */
  private detectPalmPush(_landmarks: NormalizedLandmark[], isOpenPalm: boolean): boolean {
    if (!isOpenPalm) return false;
    if (this.handSizeHistory.length < 5) return false;

    const recent = this.handSizeHistory.slice(-8);
    if (recent.length < 4) return false;

    const timeDelta = recent[recent.length - 1].timestamp - recent[0].timestamp;
    if (timeDelta > 600 || timeDelta < 100) return false;

    // Check if hand size increased significantly (palm pushed toward camera)
    const firstSize = recent[0].size;
    const lastSize = recent[recent.length - 1].size;
    const sizeIncrease = lastSize - firstSize;

    // A forward push makes the hand appear larger in the camera
    if (sizeIncrease > 0.03) {
      // Also check that palm is relatively open and steady in x-y
      const palmMovement = this.palmPositions.length >= 3 ?
        Math.abs(this.palmPositions[this.palmPositions.length - 1].x - this.palmPositions[this.palmPositions.length - 3].x) +
        Math.abs(this.palmPositions[this.palmPositions.length - 1].y - this.palmPositions[this.palmPositions.length - 3].y) : 1;

      // Not moving too much laterally (distinguishes from swipe)
      if (palmMovement < 0.15) {
        return true;
      }
    }

    return false;
  }

  private triggerGesture(gesture: SpellType, info?: GestureInfo): void {
    this.lastGestureTime = Date.now();
    this.indexFingerPath = [];
    this.palmPositions = [];
    this.openPalmStartTime = 0;
    this.handSizeHistory = [];
    this.currentProgress = null;

    const twoHandLabel = info?.twoHanded ? ' [TWO-HANDED ULTIMATE]' : '';
    console.log(`Gesture detected: ${gesture}${twoHandLabel}`, info ? `(${info.shape}, confidence: ${info.confidence.toFixed(2)}, hand: ${info.handedness || 'unknown'})` : '');

    if (this.onGestureCallback) {
      this.onGestureCallback(gesture, info);
    }
  }

  onGesture(callback: GestureCallback): void {
    this.onGestureCallback = callback;
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  getHandedness(): 'Left' | 'Right' | null {
    return this.currentHandedness;
  }

  getGestureProgress(): GestureProgress | null {
    return this.currentProgress;
  }

  /** Returns true if two hands are currently visible in the camera */
  isTwoHandsDetected(): boolean {
    return this.twoHandsDetected;
  }

  dispose(): void {
    if (this.camera) {
      this.camera.stop();
    }
    // Release video stream tracks to free webcam
    if (this.videoElement && this.videoElement.srcObject) {
      const stream = this.videoElement.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      this.videoElement.srcObject = null;
    }
    this.hands = null;
    this.camera = null;
    this.videoElement = null;
    this.canvasElement = null;
    this.canvasCtx = null;
    this.isInitialized = false;
  }
}
